import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRight, BookmarkMinus, Bookmark, MapPin, Tag } from 'lucide-react';
import * as savedService from '../services/savedService';
import type { SavedItem } from '../types/ad';

export default function SavedItemsPage(): JSX.Element {
  const { mobileOpen, toggleMobile } = useMobileMenu();
  const navigate = useNavigate();
  const [items, setItems]       = useState<SavedItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError]       = useState<string | null>(null);
  const [removing, setRemoving] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const data = await savedService.listSavedItems();
        setItems(data.items);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load saved items');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (adId: string) => {
    setRemoving(prev => new Set(prev).add(adId));
    try {
      const res = await savedService.toggleSaved(adId);
      if (!res.saved) {
        // short delay so the fade-out animation plays
        setTimeout(() => setItems(prev => prev.filter(i => i.ad.id !== adId)), 300);
      }
    } catch {
      setRemoving(prev => { const s = new Set(prev); s.delete(adId); return s; });
    }
  };

  return (
    <>
      <Helmet>
        <title>Saved Items | AdsWorld</title>
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap');
        .saved-root { font-family: 'DM Sans', sans-serif; }

        /* card */
        .saved-card {
          transition: box-shadow 0.22s ease, transform 0.22s ease, opacity 0.3s ease;
        }
        .saved-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 28px -6px rgba(0,0,0,0.09), 0 2px 8px -2px rgba(0,0,0,0.05);
        }
        .saved-card.removing { opacity: 0; transform: scale(0.97); }

        /* thumbnail zoom */
        .thumb-wrap { overflow: hidden; }
        .thumb-wrap img { transition: transform 0.4s ease; }
        .saved-card:hover .thumb-wrap img { transform: scale(1.04); }

        /* skeleton */
        @keyframes shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        .skeleton {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 600px 100%;
          animation: shimmer 1.4s infinite;
          border-radius: 8px;
        }

        /* unsave button */
        .btn-unsave {
          transition: background 0.15s, transform 0.1s, opacity 0.15s;
        }
        .btn-unsave:hover  { background: #fee2e2; }
        .btn-unsave:active { transform: scale(0.96); }
        .btn-unsave:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>

      <div className="saved-root min-h-screen bg-gray-50">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">

          {/* ── Breadcrumb ── */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-8">
            <span className="hover:text-primary-600 cursor-pointer transition-colors" onClick={() => navigate('/')}>Home</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
            <span className="hover:text-primary-600 cursor-pointer transition-colors" onClick={() => navigate('/profile')}>Profile</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
            <span className="text-gray-700 font-medium">Saved Items</span>
          </nav>

          {/* ── Page Header ── */}
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-1">
                Your wishlist
              </p>
              <h1
                className="text-3xl font-bold text-gray-900"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                Saved Items
              </h1>
            </div>
            {!isLoading && items.length > 0 && (
              <span className="text-sm font-medium text-gray-400">
                {items.length} {items.length === 1 ? 'listing' : 'listings'}
              </span>
            )}
          </div>

          {/* ── States ── */}
          {isLoading ? (
            /* Skeleton grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
                  <div className="skeleton w-full h-44" style={{ borderRadius: 0 }} />
                  <div className="p-4 space-y-2.5">
                    <div className="skeleton h-4 w-3/4" />
                    <div className="skeleton h-3 w-1/2" />
                    <div className="skeleton h-3 w-1/3" />
                    <div className="flex justify-end pt-1">
                      <div className="skeleton h-8 w-24 rounded-lg" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

          ) : error ? (
            /* Error */
            <div className="bg-white rounded-2xl border border-red-100 p-6 flex items-start gap-3">
              <span className="text-red-500 mt-0.5">⚠</span>
              <p className="text-sm text-red-600">{error}</p>
            </div>

          ) : items.length === 0 ? (
            /* Empty state */
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-20 flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                <Bookmark className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-800 mb-1">Nothing saved yet</h3>
              <p className="text-sm text-gray-400 mb-5">Browse ads and bookmark the ones you love.</p>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors"
              >
                Explore Ads
              </button>
            </div>

          ) : (
            /* Grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map(({ id, ad }) => (
                <div
                  key={id}
                  className={`saved-card bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col ${removing.has(ad.id) ? 'removing' : ''}`}
                >
                  {/* Thumbnail */}
                  <Link to={`/item/${ad.id}`} className="thumb-wrap block relative">
                    <img
                      src={ad.primary_image || '/assets/placeholder.jpg'}
                      alt={ad.title}
                      className="w-full h-44 object-cover"
                    />
                    {/* Category pill over image */}
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-700 text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
                      <Tag className="w-3 h-3" />
                      {ad.category_name}
                    </span>
                  </Link>

                  {/* Body */}
                  <div className="flex flex-col flex-1 p-4">
                    <Link to={`/item/${ad.id}`} className="block flex-1">
                      <h3 className="font-semibold text-gray-900 leading-snug line-clamp-2 mb-1">
                        {ad.title}
                      </h3>
                      {ad.location && (
                        <p className="inline-flex items-center gap-1 text-xs text-gray-400 mb-2">
                          <MapPin className="w-3 h-3" /> {ad.location}
                        </p>
                      )}
                      <p className="text-base font-bold text-gray-900 mt-auto">
                        <span className="text-xs font-semibold text-gray-400 mr-0.5">PKR</span>
                        {Number(ad.price).toLocaleString()}
                      </p>
                    </Link>

                    {/* Divider + Action */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400">
                        Saved {new Date(ad.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <button
                        onClick={() => handleToggle(ad.id)}
                        disabled={removing.has(ad.id)}
                        className="btn-unsave inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 text-red-600 text-xs font-semibold ring-1 ring-red-100"
                      >
                        <BookmarkMinus className="w-3.5 h-3.5" />
                        {removing.has(ad.id) ? 'Removing…' : 'Unsave'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        <Footer onOpenPrivacySettings={() => {}} />
      </div>
    </>
  );
}